import { Localizer } from "@/main/react/Types";
import type { LocalizationKey } from "@/main/util/LocalizationUtil";

function localize(localizer: Localizer, key: LocalizationKey, ...args: string[]): string {
    return localizer(key, ...args);
}

export type CountBadgeLabelOptions = {
    /**
     * Render the label for a count that is a KNOWN zero ("0 followers") instead of dropping the badge.
     *
     * <p>Off by default, and the default path is bit-for-bit what this helper has always done, because
     * the helper has 16 call sites and only a few of them sit on a page whose subject IS that count.
     * Suppressing a zero on a search result row or a chat suggestion is reasonable; suppressing it on
     * the owner's own followers tab removes both the answer and the navigation to the sibling tab
     * (ux-critique `EMPTY-STATES-28`). So the decision is taken per surface, one mount at a time,
     * rather than flipped for every card in the product at once.
     *
     * <p>**A missing count is never a zero, whatever this option says.** `null`, `undefined` and any
     * non-finite value still return `null`, so "we have no number for this" stays visually distinct
     * from "the number is zero" — which is the whole point of the row: today a count that failed to
     * load and a count of zero are the same silence.
     */
    showZero?: boolean;
};

export function getFriendlyCountBadgeLabel(
    localizer: Localizer,
    translationKey: LocalizationKey,
    count?: number | null,
    options?: CountBadgeLabelOptions,
): string | null {
    if (count == null) {
        return null;
    }

    const normalizedCount = Number(count);

    if (!Number.isFinite(normalizedCount) || normalizedCount < 0) {
        return null;
    }

    if (normalizedCount === 0 && options?.showZero !== true) {
        return null;
    }

    return localize(localizer, translationKey, String(normalizedCount));
}

export function getFriendlyPrivateBadgeLabel(
    localizer: Localizer,
    translationKey: LocalizationKey,
    isPrivate?: boolean | null,
): string | null {
    if (!isPrivate) {
        return null;
    }

    return localize(localizer, translationKey);
}
